<template>
  <div class="w-full">
    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <LoadingComponent />
    </div>

    <!-- Card Principal -->
    <div v-else class="bg-white rounded-lg shadow-md overflow-hidden">
      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex flex-col gap-4">
          <!-- Título e Pontuação -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 class="text-2xl font-bold text-gray-900 mb-1">
                📋 Resultados da Rodada
              </h2>
              <p class="text-sm text-gray-600">Acompanhe seu desempenho nesta rodada</p>
            </div>
            
            <!-- Pontuação Total -->
            <div class="flex items-center justify-center sm:justify-start gap-2 bg-green-50 px-4 py-2 rounded-lg border border-green-200">
              <div class="text-center">
                <p class="text-xs text-gray-600 font-medium">Pontos</p>
                <p class="text-2xl font-bold text-green-700">{{ getTotalPoints(currentRound) }}</p>
              </div>
              <div class="h-10 w-px bg-green-300"></div>
              <div class="text-center">
                <p class="text-xs text-gray-600 font-medium">Acertos</p>
                <p class="text-2xl font-bold text-green-700">{{ getTotalHits(currentRound) }}</p>
              </div>
            </div>
          </div>
          
          <!-- Ações em linha -->
          <div class="flex flex-col sm:flex-row gap-2 sm:items-center">
            <!-- Seletor de Rodada -->
            <div class="flex-1 sm:max-w-xs">
              <select 
                v-model="currentRound._id" 
                @change="onChangeSelect"
                class="block w-full px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm">
                <option v-for="round in roundIds" :key="round.value" :value="round.value">
                  {{ round.label }}
                </option>
              </select>
            </div>

            <!-- Espaçador -->
            <div class="flex-1 hidden sm:block"></div>

            <!-- Botão Mobile - Mostrar Palpites -->
            <div class="sm:hidden w-full">
              <button 
                @click="toggleBet"
                class="w-full flex items-center justify-center px-4 py-2 text-white bg-green-600 hover:bg-green-700 font-semibold text-sm rounded-lg transition-all duration-200 shadow-sm">
                <i :class="showBet ? 'fas fa-eye-slash' : 'fas fa-eye'" class="mr-2"></i>
                {{ showBet ? 'Esconder' : 'Mostrar' }} Palpites
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Grid de Partidas -->
      <div class="p-6">
        <div class="space-y-4">
          <div 
            v-for="(fixture, id) in currentRound.fixtures" 
            :key="id"
            :class="[
              'bg-white border-2 rounded-xl overflow-hidden transition-all duration-200',
              fixturePostponed(fixture.status) 
                ? 'border-red-200 bg-red-50' 
                : 'border-gray-200 hover:border-green-300 hover:shadow-md'
            ]">
            
            <!-- Layout Desktop -->
            <div class="hidden lg:grid lg:grid-cols-12 gap-4 p-4">
              <!-- Coluna do Palpite (4 colunas) -->
              <div class="col-span-4 bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                <div class="flex items-center justify-center mb-2">
                  <span class="text-xs font-semibold text-blue-700 uppercase">Seu Palpite</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex-1 text-center">
                    <span class="text-sm font-medium text-gray-700">{{ getAbbr(fixture.homeTeam.teamId) }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-2xl font-bold text-blue-700">{{ getUserInfo(fixture, "goalsHomeTeam") }}</span>
                    <span class="text-gray-400">×</span>
                    <span class="text-2xl font-bold text-blue-700">{{ getUserInfo(fixture, "goalsAwayTeam") }}</span>
                  </div>
                  <div class="flex-1 text-center">
                    <span class="text-sm font-medium text-gray-700">{{ getAbbr(fixture.awayTeam.teamId) }}</span>
                  </div>
                </div>
                <div class="mt-2 text-center">
                  <span 
                    :class="[
                      'inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold',
                      getPointsColorClass(getUserInfo(fixture, 'points'))
                    ]">
                    <i class="fas fa-trophy mr-1"></i>
                    {{ getUserInfo(fixture, "points") }} pontos
                  </span>
                </div>
              </div>

              <!-- Coluna do Resultado Real (8 colunas) -->
              <div class="col-span-8">
                <!-- Data e Status -->
                <div class="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                  <div class="flex items-center">
                    <i class="far fa-calendar-alt text-gray-400 mr-2"></i>
                    <span 
                      :class="[
                        'text-sm font-medium',
                        fixturePostponed(fixture.status) ? 'line-through text-red-600' : 'text-gray-600'
                      ]">
                      {{ formatDate(fixture.eventDate) }}
                    </span>
                  </div>
                  <span class="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {{ fixture.status }}
                  </span>
                </div>

                <!-- Confronto -->
                <div class="flex items-center justify-between gap-4">
                  <!-- Time Casa -->
                  <div class="flex-1 flex items-center justify-end gap-3">
                    <span class="font-bold text-gray-900 text-lg text-right">
                      {{ getAbbr(fixture.homeTeam.teamId) }}
                    </span>
                    <div class="flex-shrink-0">
                      <img 
                        :src="fixture.homeTeam.teamLogo" 
                        :alt="fixture.homeTeam.teamName"
                        class="h-12 w-12 object-contain"
                      />
                    </div>
                  </div>

                  <!-- Placar Real -->
                  <div class="flex items-center gap-2 px-4">
                    <span class="text-3xl font-bold text-gray-900">
                      {{ fixture.goalsHomeTeam !== null ? fixture.goalsHomeTeam : 0 }}
                    </span>
                    <span class="text-2xl font-bold text-gray-400">×</span>
                    <span class="text-3xl font-bold text-gray-900">
                      {{ fixture.goalsAwayTeam !== null ? fixture.goalsAwayTeam : 0 }}
                    </span>
                  </div>

                  <!-- Time Visitante -->
                  <div class="flex-1 flex items-center justify-start gap-3">
                    <div class="flex-shrink-0">
                      <img 
                        :src="fixture.awayTeam.teamLogo" 
                        :alt="fixture.awayTeam.teamName"
                        class="h-12 w-12 object-contain"
                      />
                    </div>
                    <span class="font-bold text-gray-900 text-lg text-left">
                      {{ getAbbr(fixture.awayTeam.teamId) }}
                    </span>
                  </div>
                </div>

                <!-- Badge de Partida Adiada -->
                <div v-if="fixturePostponed(fixture.status)" class="mt-3">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    Partida Adiada
                  </span>
                </div>
              </div>
            </div>

            <!-- Layout Mobile -->
            <div class="lg:hidden p-4">
              <!-- Data e Status -->
              <div class="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                <div class="flex items-center">
                  <i class="far fa-calendar-alt text-gray-400 mr-2 text-xs"></i>
                  <span 
                    :class="[
                      'text-xs font-medium',
                      fixturePostponed(fixture.status) ? 'line-through text-red-600' : 'text-gray-600'
                    ]">
                    {{ formatDate(fixture.eventDate) }}
                  </span>
                </div>
                <span class="text-xs font-medium text-gray-500">{{ fixture.status }}</span>
              </div>

              <!-- Confronto Mobile -->
              <div class="flex items-center justify-between gap-2 mb-3">
                <!-- Time Casa -->
                <div class="flex-1 flex flex-col items-center gap-2">
                  <img :src="fixture.homeTeam.teamLogo" class="h-10 w-10 object-contain" />
                  <span class="font-bold text-gray-900 text-sm text-center">
                    {{ getAbbr(fixture.homeTeam.teamId) }}
                  </span>
                </div>

                <!-- Placar -->
                <div class="flex flex-col items-center gap-1">
                  <div class="flex items-center gap-2">
                    <span class="text-2xl font-bold text-gray-900">
                      {{ fixture.goalsHomeTeam !== null ? fixture.goalsHomeTeam : 0 }}
                    </span>
                    <span class="text-xl font-bold text-gray-400">×</span>
                    <span class="text-2xl font-bold text-gray-900">
                      {{ fixture.goalsAwayTeam !== null ? fixture.goalsAwayTeam : 0 }}
                    </span>
                  </div>
                </div>

                <!-- Time Visitante -->
                <div class="flex-1 flex flex-col items-center gap-2">
                  <img :src="fixture.awayTeam.teamLogo" class="h-10 w-10 object-contain" />
                  <span class="font-bold text-gray-900 text-sm text-center">
                    {{ getAbbr(fixture.awayTeam.teamId) }}
                  </span>
                </div>
              </div>

              <!-- Palpite Mobile (Visível quando toggled) -->
              <div 
                v-if="showBet"
                class="mt-3 pt-3 border-t-2 border-green-300 bg-blue-50 rounded-lg p-3">
                <div class="flex items-center justify-center mb-2">
                  <span class="text-xs font-semibold text-blue-700 uppercase">Seu Palpite</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="flex-1 text-center text-sm font-medium text-gray-700">
                    {{ getAbbr(fixture.homeTeam.teamId) }}
                  </span>
                  <div class="flex items-center gap-2">
                    <span class="text-xl font-bold text-blue-700">{{ getUserInfo(fixture, "goalsHomeTeam") }}</span>
                    <span class="text-gray-400">×</span>
                    <span class="text-xl font-bold text-blue-700">{{ getUserInfo(fixture, "goalsAwayTeam") }}</span>
                  </div>
                  <span class="flex-1 text-center text-sm font-medium text-gray-700">
                    {{ getAbbr(fixture.awayTeam.teamId) }}
                  </span>
                </div>
                <div class="mt-2 text-center">
                  <span 
                    :class="[
                      'inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold',
                      getPointsColorClass(getUserInfo(fixture, 'points'))
                    ]">
                    <i class="fas fa-trophy mr-1"></i>
                    {{ getUserInfo(fixture, "points") }} pontos
                  </span>
                </div>
              </div>

              <!-- Badge Adiada Mobile -->
              <div v-if="fixturePostponed(fixture.status)" class="mt-3">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <i class="fas fa-exclamation-circle mr-1"></i>
                  Partida Adiada
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import LoadingComponent from '@/components/LoadingComponent';
import ApiService from '@/services/apiService';
import TeamInfoMap from '@/utils/teamInfoMap';
import { mapGetters } from 'vuex';

export default {
  name: 'CurrentRoundTable',
  components: {
    LoadingComponent,
  },
  props: [
    'userId',
    'roundId'
  ],
  created() {
    this.$watch(
      () => this.$route.params,
      (toParams, previousParams) => {
        console.log(toParams, previousParams);
        this.loading = true;
        this.getData(toParams.userId, toParams.roundId);
      }
    )
  },
  mounted() {
    ApiService.getRoundIds().then(rounds => {
      let options = rounds.map(data => {
        return {
          value: data._id,
          label: this.formatTitle(data._id)
        }
      });
      this.roundIds = options;
    });

    this.getData(this.userId, this.roundId);

  },
  computed: {
    ...mapGetters('user', {
      userProfile: 'userProfile',
    })
  },
  methods: {

    async getData(userId, roundId) {
      if (!roundId) {
        var currentRound = await ApiService.getCurrentRound();
        roundId = currentRound[0]._id;
      }

      ApiService.getCurrentRoundWithBets(userId, roundId).then(data => {
        this.currentRound = data;
        this.loading = false;
      }).catch(err => {
        this.currentRound._id = this.roundId;
        console.log(err);
      });
    },

    getTotalHits(currentRound) {
      if (currentRound.totalHits == undefined) {
        return 0;
      }
      return currentRound.totalHits
    },
    getTotalPoints(currentRound) {
      if (currentRound.totalPoints == undefined) {
        return 0;
      }
      return currentRound.totalPoints
    },
    getAbbr(teamId) {
      return TeamInfoMap.mapNameToAbbreviation(teamId);
    },

    onChangeSelect(event) {
      let roundId = event.target.value;
      return this.$router.push({
          name: "Current Round",
          params: { userId: this.userId, roundId: roundId }
        })
    },

    formatDate(dateString)
    {
      return new Date(dateString).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    toggleBet() {
      this.showBet = !this.showBet;
    },

    getUserInfo(fixture, info) {

      if (fixture.userBet == undefined) {
        return "-";
      }

      switch(info) {
        case 'goalsHomeTeam':
          return fixture.userBet.goalsHomeTeam !== null ? fixture.userBet.goalsHomeTeam : 0;
        case 'goalsAwayTeam':
          return fixture.userBet.goalsAwayTeam !== null ? fixture.userBet.goalsAwayTeam : 0;
        case 'points':
          return fixture.userBet.points !== null ? fixture.userBet.points : 0;
        default:
          return 0;
      }
    },

    formatTitle(title) {
      if (title !== undefined) {
        return title.replaceAll("_", " ");
      }
      return title;
    },

    fixturePostponed(status) {
      return status == 'Time to be defined' || status == "Match Postponed";
    },

    getPointsColorClass(points) {
      if (points >= 100) return 'bg-green-100 text-green-800';
      if (points >= 50) return 'bg-yellow-100 text-yellow-800';
      return 'bg-red-100 text-red-800';
    }
  },
  data(){
    return {
      currentRound: {},
      userBet: {},
      showBet: false,
      loading: true,
      value: null,
      roundIds : [],
    }
  },
}
</script>
<style>
@import "@vueform/multiselect/themes/default.css";
</style>